import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useFieldArray, useFormContext } from "react-hook-form";

import { ReactComponent as TrashIcon } from "assets/images/iconsV2/trash-01.svg";
import { useGetCountries } from "api/queries/common/useGetCountries";
import FormField from "components/molecules/FormField/FormField";
import PickupDeliveryDate from "pages/shipper/RequestForm/components/Schedule/PickupDeliveryDate";
import PortInfo from "../../Delivery/PortInfo";

const defaultVesselInfo = {
  vessel_number: "",
  vessel_arrival_date: "",
  delivery_date: "",

  arrival_city: "",
  arrival_country_code: "",
  arrival_country_name: "",
  arrival_port_code: "",
  arrival_port_name: "",

  departure_city: "",
  departure_country_code: "",
  departure_country_name: "",
  departure_port_code: "",
  departure_port_name: "",
};

interface VesselEntryProps {
  index: number;
  countries: any[];
  remove: (index: number) => void;
}

function VesselEntry({ index, countries, remove }: VesselEntryProps) {
  const intl = useIntl();
  const [holidays, setHolidays] = useState({});
  const fieldPrefix = `schedule.vessels.${index}.`;

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between">
        <p className="font-b4 mb-0">
          <FormattedMessage id="quote.provider.schedule.vessel" defaultMessage="Vessel" />
          <span className="ml-1">{index + 1}</span>
        </p>
        <span className="delete-vessel-icon" onClick={() => remove(index)}>
          <TrashIcon width={20} height={20} />
        </span>
      </div>
      <div className="flex-row mb-3">
        <div className="w-192">
          <FormField
            validations={{
              shouldUnregister: true,
            }}
            id={`id_vessel_number_${index}`}
            label={intl.formatMessage({
              id: "quote.provider.vesselNumber",
              defaultMessage: "Vessel number / IMO",
            })}
            type="textinput"
            field_id={`schedule.vessels.${index}.vessel_number`}
          />
        </div>
        <div className="w-192 align-date">
          <label>
            <FormattedMessage id="quote.provider.vesselDepartureDate" defaultMessage="Vessel departure date" />
          </label>
          <PickupDeliveryDate
            datePickerProps={{
              name: `schedule.vessels.${index}.delivery_date`,
              shouldUnregister: true,
            }}
            countries={countries}
            holidays={holidays}
            setHolidays={setHolidays}
          />
        </div>
      </div>
      <PortInfo prefix="departure" countries={countries} fieldPrefix={fieldPrefix} />

      <div className="flex-row mt-3 mb-3">
        <div className="w-192 align-date">
          <label>
            <FormattedMessage id="quote.provider.vesselArrivalDate" defaultMessage="Vessel arrival date" />
          </label>
          <PickupDeliveryDate
            datePickerProps={{
              name: `schedule.vessels.${index}.vessel_arrival_date`,
              shouldUnregister: true,
            }}
            countries={countries}
            holidays={holidays}
            setHolidays={setHolidays}
          />
        </div>
      </div>
      <PortInfo prefix="arrival" countries={countries} fieldPrefix={fieldPrefix} />
    </div>
  );
}

export default function VesselForm() {
  const methods = useFormContext();
  const { fields, append, remove } = useFieldArray({
    name: "schedule.vessels",
    control: methods.control,
    shouldUnregister: true,
  });

  const { data: countries = [] } = useGetCountries({
    refetchOnMount: false,
  });

  return (
    <>
      {fields.map((vessel, index) => (
        <div key={vessel.id} className="mb-3xl">
          <VesselEntry index={index} countries={countries} remove={remove} />
        </div>
      ))}
      <p onClick={() => append(defaultVesselInfo)} className="text-blue-200 font-b4 cursor-pointer">
        <FormattedMessage id="quote.provider.addVessel" defaultMessage="Add vessel" />
      </p>
    </>
  );
}
